import React, { SetStateAction } from "react";
import { UserDetails } from "../types/domain";
import { FormFields } from "../services/validation";
import { useTranslations } from "use-intl";

type UserPatchBuilder = (value: string) => Partial<UserDetails>;

const BuyModalForm = ({
  form,
  setForm,
  syncUserDetails,
  buildNamePatch,
  buildAddressPatch,
  buildCityPatch,
  buildStatePatch,
  buildPincodePatch,
  buildPhonePatch,
  errors,
  setErrors,
  handleSampleSubmit,
  submitting,
  loadingUser,
  submitError,
  setSubmitError,
  onBack,
  onClose
}: {
  form: {
    name: string;
    street: string;
    apartment: string;
    city: string;
    state: string;
    pincode: string;
    phone: string;
  };
  setForm: (
    value: SetStateAction<{
      name: string;
      street: string;
      apartment: string;
      city: string;
      state: string;
      pincode: string;
      phone: string;
    }>,
  ) => void;
  syncUserDetails: (patch: Partial<UserDetails>) => void;
  buildNamePatch: UserPatchBuilder;
  buildAddressPatch: UserPatchBuilder;
  buildCityPatch: UserPatchBuilder;
  buildStatePatch: UserPatchBuilder;
  buildPincodePatch: UserPatchBuilder;
  buildPhonePatch: UserPatchBuilder;
  errors: Partial<Record<keyof FormFields, string>>;
  setErrors: (
    value: SetStateAction<Partial<Record<keyof FormFields, string>>>,
  ) => void;
  handleSampleSubmit: () => void;
  submitting: boolean;
  loadingUser: boolean;
  submitError: string;
  setSubmitError: (value: SetStateAction<string>) => void;
  onBack?: () => void;
  onClose: () => void;
}) => {
  const t = useTranslations("modals");

  return (
    <div className="space-y-4">
      <div>
        <label className="ml-1 text-xs font-bold uppercase text-slate-400">
          {t("fields.recipientName")} <span className="text-red-500">*</span>
        </label>
        <input
          value={form.name}
          onChange={(e) => {
            const value = e.target.value;
            setForm({ ...form, name: value });
            syncUserDetails(buildNamePatch(value));
            setErrors((prev) => ({
              ...prev,
              name: value.trim() ? "" : `${t("fields.nameRequired")}`,
            }));
          }}
          className={`mt-1 w-full rounded-xl border px-4 py-3 text-sm ${errors.name ? "border-red-500" : "border-gray-200"
            } bg-gray-30`}
        />
        {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="ml-1 text-xs font-bold uppercase text-slate-400">
            {t("fields.streetAddress")} <span className="text-red-500">*</span>
          </label>
          <input
            value={form.street}
            onChange={(e) => {
              const value = e.target.value;
              setForm({ ...form, street: value });
              syncUserDetails(buildAddressPatch(value));
              setErrors((prev) => ({
                ...prev,
                street: value.trim() ? "" : `${t("fields.addressRequired")}`,
              }));
            }}
            className={`mt-1 w-full rounded-xl border px-4 py-3 text-sm ${errors.street ? "border-red-500" : "border-gray-200"
              } bg-gray-30`}
          />
          {errors.street && (
            <p className="text-xs text-red-500">{errors.street}</p>
          )}
        </div>

        <div>
          <label className="ml-1 text-xs font-bold uppercase text-slate-400">
            {t("fields.apartment")}
          </label>
          <input
            value={form.apartment}
            onChange={(e) => {
              const value = e.target.value;
              setForm({ ...form, apartment: value });
            }}
            placeholder={t("fields.apartmentSuite")}
            className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-30 px-4 py-3 text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="min-w-0">
          <label className="ml-1 text-[10px] font-bold uppercase text-slate-400">
            {t("fields.city")} <span className="text-red-500">*</span>
          </label>
          <input
            value={form.city}
            onChange={(e) => {
              const value = e.target.value;
              setForm({ ...form, city: value });
              syncUserDetails(buildCityPatch(value));
              setErrors((prev) => ({
                ...prev,
                city: value.trim() ? "" : `${t("fields.cityRequired")}`,
              }));
            }}
            className={`mt-1 w-full min-w-0 rounded-lg border px-2 py-2 text-sm ${errors.city ? "border-red-500" : "border-gray-200"
              } bg-gray-30`}
          />
          {errors.city && (
            <p className="mt-1 text-[10px] text-red-500">{errors.city}</p>
          )}
        </div>

        <div className="min-w-0">
          <label className="ml-1 text-[10px] font-bold uppercase text-slate-400">
            {t("fields.state")} <span className="text-red-500">*</span>
          </label>
          <input
            value={form.state}
            onChange={(e) => {
              const value = e.target.value;
              setForm({ ...form, state: value });
              syncUserDetails(buildStatePatch(value));
              setErrors((prev) => ({
                ...prev,
                state: value.trim() ? "" : `${t("fields.stateRequired")}`,
              }));
            }}
            className={`mt-1 w-full min-w-0 rounded-lg border px-2 py-2 text-sm ${errors.state ? "border-red-500" : "border-gray-200"
              } bg-gray-30`}
          />
          {errors.state && (
            <p className="mt-1 text-[10px] text-red-500">{errors.state}</p>
          )}
        </div>

        <div className="min-w-0">
          <label className="ml-1 text-[10px] font-bold uppercase text-slate-400">
            {t("fields.pincode")} <span className="text-red-500">*</span>
          </label>
          <input
            value={form.pincode}
            maxLength={10}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              setForm({ ...form, pincode: value });
              syncUserDetails(buildPincodePatch(value));
              setErrors((prev) => ({
                ...prev,
                pincode: value ? "" : `${t("fields.pincodeRequired")}`,
              }));
            }}
            className={`mt-1 w-full min-w-0 rounded-lg border px-2 py-2 text-sm ${errors.pincode ? "border-red-500" : "border-gray-200"
              } bg-gray-30`}
          />
          {errors.pincode && (
            <p className="mt-1 text-[10px] text-red-500">{errors.pincode}</p>
          )}
        </div>
      </div>

      <div className="mt-3">
        <label className="ml-1 text-xs font-bold uppercase text-slate-400">
          {t("fields.phoneNumber")} <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
            +91
          </span>
          <input
            value={form.phone}
            maxLength={10}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              setForm({ ...form, phone: value });
              syncUserDetails(buildPhonePatch(value));
              setErrors((prev) => ({
                ...prev,
                phone: value ? "" : `${t("fields.phoneRequired")}`,
              }));
            }}
            className={`w-full rounded-lg border px-2 py-2 pl-10 text-sm ${errors.phone ? "border-red-500" : "border-gray-200"
              } bg-gray-30`}
          />
        </div>
        {errors.phone && (
          <p className="mt-1 text-[10px] text-red-500">{errors.phone}</p>
        )}
      </div>

      <div className="space-y-3 pt-4">
        <button
          onClick={handleSampleSubmit}
          disabled={submitting || loadingUser}
          className="w-full rounded-xl bg-[#1F99FF] py-3 font-bold text-white disabled:opacity-60"
        >
          {submitting ? t("submitting") : t("requestSample")}
        </button>

        {submitError && (
          <p className="mt-1 text-xs text-red-600">{submitError}</p>
        )}

        <button
          onClick={() => {
            if (!submitting && !loadingUser) {
              setErrors({});
              setSubmitError("");
              if (onBack) {
                onBack();
              } else {
                onClose();
              }
            }
          }}
          disabled={submitting || loadingUser}
          className="w-full text-xs font-bold uppercase text-gray-400 hover:text-gray-600"
        >
          ← {t("back")}
        </button>
      </div>
    </div>
  );
};

export default BuyModalForm;
